import { Metrics } from "../analytics/Metrics";
import { Confetti } from "../Confetti";
import Button from "../core/Button";
import Strings from "../core/data/Strings";
import ResizableContainer from "../core/ResizableContainer";
import ScaleManager from "../core/ScaleManager";
import SoundButton from "../core/SoundButton";
import GameColors from "../GameColors";
import { GameScene } from "../scenes/GameScene";
import SessionData from "../SessionData";
import Fonts from "../utils/Fonts";
import Overlay from "../utils/Overlay";
import TweenUtils from "../utils/TweenUtils";
import Photo from "./Photo";
import QuitPopup from "./QuitPopup";

export default class Recap extends ResizableContainer {
	protected onResize?: () => any;
	private _savedImage:HTMLImageElement;
	gameScene: GameScene;
	containers: Phaser.GameObjects.Container[];
	logo: Phaser.GameObjects.Image;
	photo: Phaser.GameObjects.Image;
	menuBgs: Phaser.GameObjects.Image[];
	soundButton: SoundButton;
	buttons: Button[];
	constructor(scene: GameScene, savedImage:HTMLImageElement) {
		super(scene, 0, 0);
		Metrics.analytics.logEvent("visit_screen", { game: "ag_photobooth", screen: "recap" });
		this.gameScene = scene;
		this._savedImage = savedImage;
		const photoScale = 1.1;

		const textStyle = {
			fontFamily: Fonts.LunchBoxRegular,
			fontSize: "92px",
			color: GameColors.WHITE,
			wordWrap: { width: 300 },
			align: "center"
		};

		const bg = scene.add.image(0, 0, "gameplayBg");
		this.add(bg);
		bg.setInteractive();
		const corkBg = scene.add.image(0, 0, "corkBg");
		this.add(corkBg);
		const divider = scene.add.image(0, 0, "divider");
		this.add(divider);
		this.menuBgs = [corkBg, divider];

		const thumbBG = scene.add.image(0, 0, "takePhotoTumbBacker");
		thumbBG.setVisible(false);
		const photo = this.photo = scene.add.image(0, 0, "snapshot");

		this.add([thumbBG, photo]);

		const logo = this.logo = scene.add.image(0, 0, "recapLogoDeco");

		const overlay = Overlay.create(scene, 0);
		overlay.tint = 0x3a2454;
		const confettiContainer = scene.add.container();
		confettiContainer.setSize(scene.viewport.width, scene.viewport.height);
		const confetti = Confetti.create(scene, confettiContainer);
		const headerContainer = scene.add.container();
		const headerImg = scene.add.image(0, 0, "recapHeader");
		const headerNumber = SessionData.Instance.endMessages.splice(0, 1)[0];
		// Prevent 1/5 chance of repeat after refresh
		if (SessionData.Instance.endMessages.length == 0) {
			SessionData.Instance.refreshEndMessages();
			const indexOfHN = SessionData.Instance.endMessages.indexOf(headerNumber);
			SessionData.Instance.endMessages.splice(indexOfHN, 1);
		}
		const headerStr = Strings.get("RecapHeader" + headerNumber);
		const header = scene.add.text(0, 0, headerStr, textStyle);
		delete textStyle.wordWrap;
		headerContainer.add([headerImg, header]);
		headerContainer.setSize(headerImg.width, headerImg.height);
		textStyle.fontSize = "38px";

		header.setFontSize(70);
		const saveContainer = scene.add.container();
		const saveButton = scene.add.button(0, 0, "saveBtn", undefined, this.saveSnapshot, this, true);
		const saveText = scene.add.text(0, 0, Strings.get("Save"), textStyle);
		saveContainer.add([saveButton, saveText]);
		// saveContainer.setSize(saveButton.width, saveButton.height);
		scene.alignIn.BottomCenter(saveText, saveButton, 0, 30);
		saveButton.addPointerDownAnim(TweenUtils.BounceContainer(scene, saveContainer));

		const newContainer = scene.add.container();
		const newButton = scene.add.button(0, 0, "newBtn", undefined, this.newPhoto, this, true);
		const newText = scene.add.text(0, 0, Strings.get("New"), textStyle);
		newContainer.add([newButton, newText]);
		// newContainer.setSize(newButton.width, newButton.height);
		scene.alignIn.BottomCenter(newText, newButton, 0, 30);
		newButton.addPointerDownAnim(TweenUtils.BounceContainer(scene, newContainer));

		const editContainer = scene.add.container();
		const editButton = scene.add.button(0, 0, "editBtn", undefined, this.resumeEdit, this, true);
		const editText = scene.add.text(0, 0, Strings.get("Edit"), textStyle);
		editContainer.add([editButton, editText]);
		// editContainer.setSize(editButton.width, editButton.height);
		scene.alignIn.BottomCenter(editText, editButton, 0, 30);
		editButton.addPointerDownAnim(TweenUtils.BounceContainer(scene, editContainer));

		const soundImg = scene.sound.mute ? "soundBtnOff" : "soundBtnOn";
        const soundButton = this.soundButton = new SoundButton(scene, 0, 0, soundImg, (mute) => {
            soundButton.setTexture(mute ? "soundBtnOff" : "soundBtnOn");
			scene.soundButton.setTexture(mute ? "soundBtnOff" : "soundBtnOn");
			scene.soundButton.updateMute(mute);
        });

		this.add([logo, photo, overlay, confettiContainer, headerContainer, saveContainer, newContainer, editContainer, soundButton]);

		this.addResize((): void => {
			const edgeOffset = 32;
			const buttonOffset = 50;

			scene.alignIn.TopRight(soundButton, scene.viewport, -18, -15);
			scene.alignIn.Center(photo, scene.viewport, 360 - 32 * 2 - 109, 0); //360 is gamescene menu display width
			const screenRatio = scene.viewport.width / scene.viewport.height;
            const idealRatio = 16/9;
			let scale = 1.1;
			if (screenRatio < idealRatio) scale = 1 - (idealRatio - screenRatio);
			photo.scale = scale;
			if(photo instanceof Photo) photo.resize();

			scene.alignIn.Center(photo, scene.viewport, 185, -50);
			scene.alignIn.BottomCenter(logo, scene.viewport, 185, -8);
			scene.alignIn.LeftCenter(corkBg, scene.viewport, 0, 0);
			scene.alignIn.RightCenter(divider, corkBg, 42);
			ScaleManager.ScaleBackgroundFromZone(bg, scene.viewport);
			scene.alignIn.Center(bg, scene.viewport);
			const headerYOffset = -edgeOffset - soundButton.height - buttonOffset;
			const headerXOffset = -edgeOffset - buttonOffset - (newContainer.width - header.width) / 2;
			scene.alignIn.TopCenter(headerContainer, corkBg, 0, -7);
			scene.alignIn.Center(header, headerImg, 0, -60);
			scene.alignIn.Center(confettiContainer, scene.viewport, 0, 0);

			scene.alignIn.Center(editContainer, corkBg, 0, 80);
			scene.alignIn.BottomCenter(newContainer, editContainer, 0, 125);
			scene.alignTo.TopCenter(saveContainer, editContainer, 0, 125);

		});

		// Build-in animation
		scene.tweens.add({
			targets: overlay,
			duration: 300,
			alpha: 0.8,
			ease: Phaser.Math.Easing.Quadratic.Out,
			onComplete: () => {
				scene.tweens.add({
					targets: overlay,
					duration: 600,
					alpha: 0,
					ease: Phaser.Math.Easing.Quadratic.In,
					delay: 1300,
					onUpdate: () => {
						for (const confet of confetti) confet.setAlpha(overlay.alpha);
					}
				});
			}
		});

		logo.setScale(0);
		scene.tweens.add({
			targets: logo,
			duration: 300,
			scale: 1,
			ease: Phaser.Math.Easing.Back.Out,
		});

		this.containers = [headerContainer, saveContainer, editContainer, newContainer];
		this.buttons = [saveButton, editButton, soundButton, newButton];
		for (let i = 0; i < this.containers.length; i++) {
			const container = this.containers[i];
			container.setAlpha(0);
			scene.tweens.add({
				targets: container,
				duration: 300,
				scale: {from: 0, to: 1},
				ease: Phaser.Math.Easing.Back.Out,
				delay: 75 * i,
				onStart: () => {
					container.setScale(0);
					container.setAlpha(1);
				}
			});
		}
	}

	tweenOut(onComplete: Function): void {
		for (let i = 0; i < this.containers.length; i++) {
			const container = this.containers[i];
			this.scene.tweens.add({
				targets: container,
				duration: 300,
				scale: {from: 1, to: 0},
				ease: Phaser.Math.Easing.Back.In,
				delay: 75 * i,
			});
		}

		for (const bg of this.menuBgs) {
			this.scene.tweens.add({
				targets: bg,
				x: bg.x - this.scene.viewport.width/2,
				ease: Phaser.Math.Easing.Quadratic.In,
				delay: 300,
				duration: 400,
			});
		}

		this.scene.tweens.add({
			targets: this.logo,
			duration: 300,
			scale: 0,
			ease: Phaser.Math.Easing.Back.In,
		});

		
		this.scene.tweens.add({
			targets: this.soundButton,
			duration: 300,
			scale: 0,
			delay: 75,
			ease: Phaser.Math.Easing.Back.In,
		});

		this.scene.tweens.add({
			targets: this.photo,
			duration: 300,
			scale: 0,
			angle: 80,
			delay: 100,
			ease: Phaser.Math.Easing.Back.In,
		});

		this.scene.time.addEvent({
			delay: 700,
			callback: () => onComplete()
		});
	}

	protected saveSnapshot(): void {
		const image = this._savedImage;
		(<any>window).saveAs(image.src, "My AG Photo.png");
		Metrics.analytics.logEvent("image_saved", { game: "ag_photobooth" });
		// this.scene.soundManager.play('snapshot');
	}

	protected resumeEdit(): void {
		// if (this.scene.textures.exists("snapshot")) {
		// 	this.scene.textures.remove("snapshot");
		// }
		//this.gameScene.toggleInteractiveButtons(true);
		for (const button of this.buttons) button.disableInteractive();
		for (let i = 0; i < this.containers.length; i++) {
			const container = this.containers[i];
			this.scene.tweens.add({
				targets: container,
				duration: 300,
				scale: {from: 1, to: 0},
				ease: Phaser.Math.Easing.Back.In,
				delay: 75 * i,
			});
		}

		this.scene.tweens.add({
			targets: this.logo,
			duration: 300,
			scale: 0,
			ease: Phaser.Math.Easing.Back.In,
		});

		this.scene.time.addEvent({
			delay: 700,
			callback: () => {
				this.gameScene.resumeFromRecap();
				this.destroy();
			}
		});
	}

	protected newPhoto(): void {
		const pop =  new QuitPopup(this.scene, this);
		this.scene.add.existing(pop);
	}
}